import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { FONT_SCALE_STEPS } from "../generated/tokens.js";
export const DEFAULT_READING_PREFS = { fontScale: "A", highContrast: false };
export const READING_PREFS_STORAGE_KEY = "we:leitura";
export function isFontScale(value) {
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(FONT_SCALE_STEPS, value);
}
export function normalizeReadingPrefs(raw) {
    const x = (raw ?? {});
    return {
        fontScale: isFontScale(x.fontScale) ? x.fontScale : DEFAULT_READING_PREFS.fontScale,
        highContrast: x.highContrast === true,
    };
}
export function applyReadingPrefs(prefs, root = document.documentElement) {
    if (prefs.fontScale === "A")
        root.removeAttribute("data-letra");
    else
        root.setAttribute("data-letra", prefs.fontScale);
    if (prefs.highContrast)
        root.setAttribute("data-contraste", "alto");
    else
        root.removeAttribute("data-contraste");
}
export function readStoredReadingPrefs() {
    try {
        return normalizeReadingPrefs(JSON.parse(localStorage.getItem(READING_PREFS_STORAGE_KEY) ?? "null"));
    }
    catch {
        return DEFAULT_READING_PREFS;
    }
}
/**
 * Script para o <head> do index.html: aplica a escolha guardada antes da primeira pintura, sem piscar
 * a letra padrão para quem usa A++ ou alto contraste.
 */
export const READING_PREFS_BOOT_SCRIPT = `try{var p=JSON.parse(localStorage.getItem(${JSON.stringify(READING_PREFS_STORAGE_KEY)})||"null")||{},r=document.documentElement;if(p.fontScale==="A+"||p.fontScale==="A++")r.setAttribute("data-letra",p.fontScale);if(p.highContrast===true)r.setAttribute("data-contraste","alto")}catch(e){}`;
/** No servidor (página gerada no build, D113) não há `useLayoutEffect`; lá nenhum efeito roda. */
const useLayoutEffectNoNavegador = typeof window === "undefined" ? useEffect : useLayoutEffect;
export function useReadingPrefs(options = {}) {
    const { initial, onChange } = options;
    // O primeiro desenho é igual ao do HTML gerado no build (letra padrão), para o React acordar por cima
    // dele sem divergência. A escolha guardada no aparelho entra logo depois, antes da pintura; a letra
    // na tela já estava certa, aplicada pelo READING_PREFS_BOOT_SCRIPT no <head>.
    const [prefs, setPrefs] = useState(() => initial ?? DEFAULT_READING_PREFS);
    const [lida, setLida] = useState(false);
    useLayoutEffectNoNavegador(() => {
        if (!initial)
            setPrefs(readStoredReadingPrefs());
        setLida(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Quando o perfil chega, a escolha salva na conta vale (D84: "vale em todas as telas").
    useEffect(() => {
        if (initial)
            setPrefs(normalizeReadingPrefs(initial));
    }, [initial?.fontScale, initial?.highContrast]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Antes de ler o aparelho, gravar a letra padrão apagaria a escolha guardada.
        if (!lida)
            return;
        applyReadingPrefs(prefs);
        try {
            localStorage.setItem(READING_PREFS_STORAGE_KEY, JSON.stringify(prefs));
        }
        catch {
            // Sem armazenamento no aparelho: a escolha vale até fechar a página e continua no perfil.
        }
    }, [prefs, lida]);
    const update = useCallback((next) => {
        const merged = normalizeReadingPrefs({ ...prefs, ...next });
        setPrefs(merged);
        onChange?.(merged);
    }, [prefs, onChange]);
    return { prefs, update };
}
